import React from "react";
import { getCsrfToken } from "../util";

// TWO contexts, not one, and the split is the whole point.
//
// Everything used to hang off a single value that changed on every cart mutation. The FNB page has 90
// menu items, each memoized but each also calling useOrder() — and React.memo cannot stop a context
// change. So one keystroke in a cart note re-rendered all 90 items, their images, icons and styled
// components, and each one re-scanned the cart with a find(). That is the lag.
//
// OrderActionsContext holds only functions and is built once with no dependencies, so a component
// that just needs "add to cart" subscribes to something that never changes and never re-renders.
// OrderStateContext holds the data that genuinely changes. Components take the narrowest one they can.
const OrderStateContext = React.createContext(undefined);
const OrderActionsContext = React.createContext(undefined);

const cartTotalOf = (cart) =>
  cart.reduce(
    (total, item) => total + Number(item?.price?.amount || 0) * item.quantity,
    0,
  );

const cartCountOf = (cart) =>
  cart.reduce((count, item) => count + item.quantity, 0);

export const OrderProvider = ({ children }) => {
  const [cart, setCart] = React.useState([]);
  const [currentOrder, setCurrentOrder] = React.useState(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  // Deep-link voucher (`?vca=<coupon_id>`): captured on the FNB page, auto-applied at checkout.
  // Persisted to localStorage so it survives a reload / re-open between the FNB page and checkout.
  const [voucherCouponId, setVoucherCouponId] = React.useState(
    () => localStorage.getItem("hebe-voucher-vca") || null,
  );

  // Lets the actions read the current cart without depending on it. Without this, processPayment and
  // the getters would be rebuilt on every mutation and the "stable" actions context would not be
  // stable at all.
  //
  // Assigned during render, NOT in an effect. Effects run after children render, so a consumer that
  // calls getCartTotal() while rendering — the checkout page does, for the amount the guest is about
  // to pay — would read the previous cart and display a stale total.
  const cartRef = React.useRef(cart);
  cartRef.current = cart;

  React.useEffect(() => {
    if (voucherCouponId) {
      localStorage.setItem("hebe-voucher-vca", voucherCouponId);
    } else {
      localStorage.removeItem("hebe-voucher-vca");
    }
  }, [voucherCouponId]);

  const addToCart = React.useCallback((item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem,
        );
      }
      return [...prevCart, { ...item, quantity: 1 }];
    });
  }, []);

  const removeFromCart = React.useCallback((itemId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== itemId));
  }, []);

  const updateCartQuantity = React.useCallback(
    (itemId, quantity, itemPayload = null) => {
      if (quantity <= 0) {
        setCart((prevCart) => prevCart.filter((item) => item.id !== itemId));
        return;
      }
      setCart((prevCart) => {
        const existingItem = prevCart.find((item) => item.id === itemId);
        if (!existingItem) {
          if (!itemPayload) return prevCart;
          return [...prevCart, { ...itemPayload, quantity }];
        }
        // Returning prevCart unchanged when the quantity already matches keeps a repeated tap from
        // publishing a new array and waking every subscriber for nothing.
        if (existingItem.quantity === quantity) return prevCart;

        return prevCart.map((item) =>
          item.id === itemId ? { ...item, quantity } : item,
        );
      });
    },
    [],
  );

  const clearCart = React.useCallback(() => {
    setCart([]);
  }, []);

  const updateCartItemNote = React.useCallback((itemId, note) => {
    const normalizedNote = note.toString().slice(0, 200);
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === itemId);
      if (!existingItem || existingItem.note === normalizedNote)
        return prevCart;

      return prevCart.map((item) =>
        item.id === itemId ? { ...item, note: normalizedNote } : item,
      );
    });
  }, []);

  // Kept as functions for the callers that already use them, but reading the ref rather than closing
  // over cart — so they keep one identity for the life of the provider.
  const getCartTotal = React.useCallback(
    () => cartTotalOf(cartRef.current),
    [],
  );
  const getCartItemCount = React.useCallback(
    () => cartCountOf(cartRef.current),
    [],
  );

  const processPayment = React.useCallback(
    async ({
      userName,
      buildingCode,
      roomNumber,
      webPushSubscriptionId,
      email,
      voucherCode,
    }) => {
      setIsProcessing(true);
      try {
        const lines = cartRef.current.map((item) => ({
          orderable_type: item.orderableType || "FnbMenuItem",
          orderable_id: item.id,
          quantity: item.quantity,
          note: item.note || null,
        }));

        const orderPayload = {
          user_name: userName,
          building_code: buildingCode,
          room_number: roomNumber || null,
          email: email || null,
          lines,
        };
        if (webPushSubscriptionId) {
          orderPayload.web_push_subscription_id = webPushSubscriptionId;
        }
        if (voucherCode) {
          orderPayload.voucher_code = voucherCode;
        }

        const response = await fetch("/api/v1/orders", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
            Accept: "application/json",
          },
          body: JSON.stringify({ order: orderPayload }),
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.message || "Failed to create order");
        }

        const newOrder = payload?.data;
        setCurrentOrder(newOrder);
        setCart([]);
        setVoucherCouponId(null);
        return newOrder;
      } finally {
        setIsProcessing(false);
      }
    },
    [],
  );

  const resetOrder = React.useCallback(() => {
    setCurrentOrder(null);
  }, []);

  // No dependencies: every member is either a setState or a useCallback with an empty dep list, so
  // this object is created once and consumers of it never re-render because of the cart.
  const actions = React.useMemo(
    () => ({
      addToCart,
      removeFromCart,
      updateCartQuantity,
      clearCart,
      updateCartItemNote,
      getCartTotal,
      getCartItemCount,
      processPayment,
      resetOrder,
      setVoucherCouponId,
    }),
    [
      addToCart,
      removeFromCart,
      updateCartQuantity,
      clearCart,
      updateCartItemNote,
      getCartTotal,
      getCartItemCount,
      processPayment,
      resetOrder,
    ],
  );

  // Totals are computed once per cart change here, instead of every consumer recomputing a reduce on
  // every render of its own.
  const state = React.useMemo(
    () => ({
      cart,
      currentOrder,
      isProcessing,
      voucherCouponId,
      cartTotal: cartTotalOf(cart),
      cartCount: cartCountOf(cart),
    }),
    [cart, currentOrder, isProcessing, voucherCouponId],
  );

  return (
    <OrderActionsContext.Provider value={actions}>
      <OrderStateContext.Provider value={state}>
        {children}
      </OrderStateContext.Provider>
    </OrderActionsContext.Provider>
  );
};

const useOrderState = () => {
  const context = React.use(OrderStateContext);
  if (context === undefined) {
    throw new Error("useOrderState must be used within an OrderProvider");
  }
  return context;
};

// Actions only. Prefer this in anything rendered in a list — it is the difference between 90 menu
// items re-rendering on a keystroke and none of them.
export const useOrderActions = () => {
  const context = React.use(OrderActionsContext);
  if (context === undefined) {
    throw new Error("useOrderActions must be used within an OrderProvider");
  }
  return context;
};

// Just the money, for badges and summaries that do not need the lines themselves.
export const useCartSummary = () => {
  const { cartTotal, cartCount } = useOrderState();
  return React.useMemo(
    () => ({ cartTotal, cartCount }),
    [cartTotal, cartCount],
  );
};

export const useCart = () => useOrderState().cart;

// Everything at once. Fine for a page that genuinely needs the lines and the actions (checkout); a
// component inside a list should take the narrower hooks above instead.
export const useOrder = () => {
  const state = useOrderState();
  const actions = useOrderActions();
  return React.useMemo(() => ({ ...state, ...actions }), [state, actions]);
};
